import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Briefcase, RefreshCw, TrendingUp, AlertTriangle, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_LABEL = { bozza: "Bozza", attiva: "Attiva", pausa: "In pausa", archiviata: "Archiviata" };
const STATUS_STYLE = {
  bozza: "bg-background text-subtle border-line",
  attiva: "bg-brand-soft text-brand-dark border-transparent",
  pausa: "bg-[#FFF4E5] text-[#B45309] border-transparent",
  archiviata: "bg-[#F0EFEB] text-faint border-line",
};
const HISTORY_OPTIONS = [7, 15, 30, 60, 90];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function RefreshDialog({ position, open, onClose, onDone }) {
  const [days, setDays] = useState(15);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => { if (!open) { setPreview(null); setDays(15); } }, [open]);

  const calc = async () => {
    setLoadingPreview(true); setPreview(null);
    try {
      const { data } = await api.post(`/positions/${position.id}/refresh-preview`, { history_days: days });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingPreview(false); }
  };

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/positions/${position.id}/refresh`, { history_days: days });
      if ((data.new_opportunities || 0) === 0) {
        if (data.rate_limited) toast.error("Limite orario TheirStack raggiunto (50/ora). Nessun nuovo annuncio importato: riprova tra qualche minuto.");
        else if (data.error) toast.error("Errore TheirStack: nessun annuncio importato. Riprova più tardi.");
        else toast.info("Nessun nuovo annuncio dall'ultimo import.");
      } else {
        toast.success(`Aggiornamento completato: ${data.new_opportunities} nuove opportunità (${data.jobs_processed} annunci processati)`);
      }
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  if (!position) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white border-line rounded-[8px]">
        <DialogHeader><DialogTitle>Aggiorna "{position.name}"</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-4">
          <p className="text-xs text-subtle">
            Importa solo gli annunci <b>nuovi</b> scoperti da TheirStack dopo l'ultimo aggiornamento
            (<b>{fmtDate(position.last_import_at || position.activated_at)}</b>). Nessun duplicato, nessun credito sprecato.
          </p>
          <div>
            <label className="text-sm font-semibold text-ink block mb-1.5">Freschezza annunci (pubblicati negli ultimi)</label>
            <select data-testid="refresh-days" value={days} onChange={(e) => { setDays(parseInt(e.target.value)); setPreview(null); }} className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors">
              {HISTORY_OPTIONS.map((d) => <option key={d} value={d}>{d} giorni</option>)}
            </select>
          </div>

          <button data-testid="refresh-calc-button" onClick={calc} disabled={loadingPreview} className="flex items-center justify-center gap-1.5 bg-white border border-line text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-background disabled:opacity-50 transition-colors">
            <TrendingUp className="w-4 h-4" strokeWidth={1.5} /> {loadingPreview ? "Calcolo…" : "Calcola nuovi annunci (gratis)"}
          </button>

          {preview && (
            <div data-testid="refresh-preview" className="flex flex-col gap-2 rounded-[6px] border border-line p-3 text-xs">
              {preview.used_mock ? (
                <div className="flex items-start gap-2 text-destructive">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span>{preview.rate_limited ? "Limite orario TheirStack raggiunto (50/ora). Riprova tra qualche minuto." : "Stima non disponibile ora. Riprova più tardi."}</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-subtle">Nuovi annunci trovati</span><span className="font-semibold text-ink">{preview.new_ads}</span></div>
                  <div className="flex justify-between"><span className="text-subtle">Annunci importati</span><span className="font-medium text-ink">{Math.min(preview.new_ads, preview.import_limit)}</span></div>
                  <div className="flex justify-between"><span className="text-subtle">Costo importazione</span><span className="font-semibold text-brand-dark">{preview.estimated_cost_eur}€ ({preview.estimated_credits} crediti)</span></div>
                  {preview.capped && (
                    <div className="flex items-start gap-2 bg-[#FFF4E5] text-[#B45309] rounded-[6px] p-2 mt-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span>Limite di piattaforma attivo: verranno importati i primi <b>{preview.import_limit}</b> dei {preview.new_ads} annunci trovati. (Modificabile in Amministrazione → Impostazioni)</span>
                    </div>
                  )}
                  {preview.new_ads === 0 && <p className="text-faint">Nessun nuovo annuncio dall'ultimo aggiornamento.</p>}
                </>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="bg-white border border-line text-ink text-sm rounded-[6px] px-4 py-2 hover:bg-background transition-colors">Annulla</button>
          <button
            data-testid="refresh-confirm-button"
            onClick={run}
            disabled={running || !preview || preview.used_mock || preview.new_ads === 0}
            className="flex items-center gap-1.5 bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover disabled:opacity-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> {running ? "Importazione…" : "Conferma importazione"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Positions() {
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [refreshTarget, setRefreshTarget] = useState(null);
  const load = () => api.get("/positions").then((r) => setPositions(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Eliminare la posizione?")) return;
    await api.delete(`/positions/${id}`);
    toast.success("Posizione eliminata");
    load();
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/positions/${id}/duplicate`);
      toast.success("Posizione duplicata");
      navigate(`/posizioni/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Posizioni di ricerca</h1>
          <p className="text-xs text-subtle mt-0.5">Configura i criteri di ricerca annunci e attiva il fetch.</p>
        </div>
        <button data-testid="new-position-button" onClick={() => navigate("/posizioni/nuova")} className="flex items-center gap-1.5 bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover transition-colors">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Nuova posizione
        </button>
      </div>

      <div className="bg-white border border-line rounded-[8px] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs text-subtle">
              <th className="px-4 py-2.5 font-semibold">Nome</th>
              <th className="px-4 py-2.5 font-semibold">Keyword</th>
              <th className="px-4 py-2.5 font-semibold">Storico</th>
              <th className="px-4 py-2.5 font-semibold">Opp.</th>
              <th className="px-4 py-2.5 font-semibold">Stato</th>
              <th className="px-4 py-2.5 font-semibold text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((p) => (
              <tr key={p.id} data-testid={`position-row-${p.id}`} className="border-b border-line last:border-0 hover:bg-background transition-colors">
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-xs text-subtle">{(p.keywords || []).slice(0, 3).join(", ")}{p.keywords?.length > 3 ? "…" : ""}</td>
                <td className="px-4 py-3 text-xs">{p.history_days}g</td>
                <td className="px-4 py-3 text-xs">{p.opportunities_count}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] font-medium border rounded-full px-2 py-0.5 ${STATUS_STYLE[p.status]}`}>{STATUS_LABEL[p.status] || p.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    {p.status === "attiva" && (
                      <button data-testid={`refresh-${p.id}`} onClick={() => setRefreshTarget(p)} title="Aggiorna: importa nuovi annunci" className="p-1.5 rounded-[6px] hover:bg-background text-subtle hover:text-brand-dark transition-colors"><RefreshCw className="w-4 h-4" strokeWidth={1.5} /></button>
                    )}
                    <button data-testid={`edit-${p.id}`} onClick={() => navigate(`/posizioni/${p.id}`)} className="p-1.5 rounded-[6px] hover:bg-background text-subtle transition-colors"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                    <button data-testid={`duplicate-${p.id}`} onClick={() => duplicate(p.id)} title="Duplica posizione" className="p-1.5 rounded-[6px] hover:bg-background text-subtle hover:text-brand-dark transition-colors"><Copy className="w-4 h-4" strokeWidth={1.5} /></button>
                    <button data-testid={`delete-${p.id}`} onClick={() => remove(p.id)} className="p-1.5 rounded-[6px] hover:bg-background text-subtle hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {positions.length === 0 && (
          <div className="py-12 text-center">
            <Briefcase className="w-8 h-8 text-faint mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-sm text-faint">Nessuna posizione. Creane una per iniziare.</p>
          </div>
        )}
      </div>

      <RefreshDialog
        position={refreshTarget}
        open={!!refreshTarget}
        onClose={() => setRefreshTarget(null)}
        onDone={() => { setRefreshTarget(null); load(); }}
      />
    </div>
  );
}
